import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { siteConfig } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  usePageTitle('Contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Hero Section - Dark */}
      <section className="bg-brand-dark py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl lg:text-4xl font-semibold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-gray-400">
              Get in touch to learn more about our programs or how you can get involved.
            </p>
            <p className="text-brand-gold/80 text-sm font-medium tracking-wide mt-4">You are not alone.</p>
          </div>
        </div>
      </section>

      {/* Contact Form - Form First */}
      <section className="bg-brand-light py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div className="max-w-lg">
              <h2 className="text-2xl font-semibold text-brand-dark mb-8">
                Send us a message
              </h2>

              {submitted ? (
                <div className="bg-white p-8">
                  <p className="text-brand-dark font-medium mb-2">Thank you for reaching out.</p>
                  <p className="text-gray-600">We will get back to you as soon as possible.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-brand-gold font-medium hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-brand-dark font-medium">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-brand-dark"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-brand-dark font-medium">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-brand-dark"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-brand-dark font-medium">
                      Phone (optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-brand-dark"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-brand-dark font-medium">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-brand-dark resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-brand-gold px-8 py-3 text-base font-semibold text-brand-dark transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-semibold text-brand-dark mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-brand-dark uppercase tracking-wide mb-1">
                    Email
                  </h3>
                  <p className="text-gray-700">{siteConfig.contact.email}</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-brand-dark uppercase tracking-wide mb-1">
                    Phone
                  </h3>
                  <p className="text-gray-700">{siteConfig.contact.phone}</p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-brand-dark uppercase tracking-wide mb-1">
                    Location
                  </h3>
                  <p className="text-gray-700">{siteConfig.contact.address}</p>
                </div>
              </div>

              {/* Support Image */}
              <div className="mt-12">
                <img
                  src="/images/site/contact.png"
                  alt="You Are Not Alone - ElevateMN"
                  className="w-48 h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
